import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  AccountabilityDefinition,
  AccountabilityDefinitionRequest,
  UpdateAccountabilityDefinitionRequest,
} from 'types/api/SystemTenant/AROChart/AccountabilityDefinitionTypes';

export const ACCOUNTABILITY_DEFINITION_QUERY_KEY = {
  LIST: 'ACCOUNTABILITY_DEFINITION_LIST',
  DETAILS: 'ACCOUNTABILITY_DEFINITION_DETAILS',
};

const ACCOUNTABILITY_DEFINITION_BASE_PATH = '/api/v1/accountability-definition';

export function useGetAccountabilityDefinitions(kTreeDefinitionId?: string, enabled = true) {
  return useQuery<AxiosResponse<AccountabilityDefinition[]>, AxiosDefaultErrorEntity>(
    [ACCOUNTABILITY_DEFINITION_QUERY_KEY.LIST, kTreeDefinitionId],
    () =>
      axios
        .get(ACCOUNTABILITY_DEFINITION_BASE_PATH, {
          params: kTreeDefinitionId ? { kTreeDefinitionId } : undefined,
        })
        .then((res) => res),
    { enabled }
  );
}

export function useGetAccountabilityDefinition(id?: string) {
  return useQuery<AxiosResponse<AccountabilityDefinition>, AxiosDefaultErrorEntity>(
    [ACCOUNTABILITY_DEFINITION_QUERY_KEY.DETAILS, id],
    () => axios.get(`${ACCOUNTABILITY_DEFINITION_BASE_PATH}/${id}`).then((res) => res),
    { enabled: Boolean(id) }
  );
}

export function useCreateAccountabilityDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccountabilityDefinition>, AxiosDefaultErrorEntity, AccountabilityDefinitionRequest>(
    (data) => axios.post(ACCOUNTABILITY_DEFINITION_BASE_PATH, data).then((res) => res),
    {
      onSuccess: () => queryClient.invalidateQueries(ACCOUNTABILITY_DEFINITION_QUERY_KEY.LIST),
    }
  );
}

export function useUpdateAccountabilityDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<AccountabilityDefinition>,
    AxiosDefaultErrorEntity,
    UpdateAccountabilityDefinitionRequest
  >(({ id, data }) => axios.put(`${ACCOUNTABILITY_DEFINITION_BASE_PATH}/${id}`, data).then((res) => res), {
    onSuccess: () => {
      queryClient.invalidateQueries(ACCOUNTABILITY_DEFINITION_QUERY_KEY.LIST);
      queryClient.invalidateQueries(ACCOUNTABILITY_DEFINITION_QUERY_KEY.DETAILS);
    },
  });
}

export function useDeleteAccountabilityDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<void>, AxiosDefaultErrorEntity, string>(
    (id) => axios.delete(`${ACCOUNTABILITY_DEFINITION_BASE_PATH}/${id}`).then((res) => res),
    {
      onSuccess: () => queryClient.invalidateQueries(ACCOUNTABILITY_DEFINITION_QUERY_KEY.LIST),
    }
  );
}
